import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import MarketplaceVisibilityBanner from '../MarketplaceVisibilityBanner';
import './ProductWizard.css';
import '../WizardProgress.css';
import ProductWizardBasics from './ProductWizardBasics';
import ProductWizardDetails from './ProductWizardDetails';
import ProductWizardCompliance from './ProductWizardCompliance';
import ProductWizardMedia from './ProductWizardMedia';
import ProductWizardStock from './ProductWizardStock';
import ProductWizardShipping from './ProductWizardShipping';
import ProductWizardPublish from './ProductWizardPublish';

const STOCK_TYPES = ['physical', 'service', 'custom', 'commission', 'digital'];
const SHIPPED_TYPES = ['physical', 'commission'];

const readApiResponse = async (res) => {
    const text = await res.text();
    if (!text) return {};

    try {
        return JSON.parse(text);
    } catch (e) {
        return { error: text, message: text };
    }
};

const messageFromApiResponse = (body, fallback) => (
    body?.error || body?.message || fallback
);

function WizardProgressBar({ steps, currentStep, completedSteps, onStepClick }) {
    const currentStepRef = useRef(null);

    useEffect(() => {
        currentStepRef.current?.scrollIntoView?.({
            block: 'nearest',
            inline: 'center',
        });
    }, [currentStep, steps.length]);

    return (
        <nav className="wizard-progress" aria-label="Product setup progress">
            {steps.map((step, idx) => {
                const isActive = idx === currentStep;
                const isDone = completedSteps.has(idx);
                const isClickable = isDone && !isActive;
                const className = [
                    'wizard-progress-step',
                    isActive ? 'current' : '',
                    isDone ? 'done' : '',
                    isClickable ? 'clickable' : '',
                ].filter(Boolean).join(' ');
                return (
                    <button
                        key={step}
                        ref={isActive ? currentStepRef : undefined}
                        type="button"
                        className={className}
                        onClick={isClickable ? () => onStepClick(idx) : undefined}
                        disabled={!isClickable}
                        aria-label={`${idx + 1}. ${step}`}
                        aria-current={isActive ? 'step' : undefined}
                        title={isClickable ? `Jump to ${step}` : undefined}
                    >
                        <span className="wizard-progress-dot" aria-hidden="true">
                            {idx + 1}
                        </span>
                        <span>{step}</span>
                    </button>
                );
            })}
        </nav>
    );
}

export default function ProductWizard({ productUuid, modal = false, onComplete, onClose }) {
    const { fetch, API_URL, channel } = useAuth();
    const isRed = channel?.isRed;

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(!!productUuid);
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const didPrePopulate = useRef(false);
    const saveQueue = useRef(Promise.resolve());

    const steps = useMemo(() => {
        const base = ['Basics', 'Details'];
        base.push('Media');
        if (STOCK_TYPES.includes(product?.type)) base.push('Stock');
        if (SHIPPED_TYPES.includes(product?.type)) base.push('Shipping');
        if (isRed) base.push('Compliance');
        base.push('Publish');
        return base;
    }, [product?.type, isRed]);

    // When editing an existing product, mark all steps as reachable immediately
    // so the user can jump between them via the progress bar.
    useEffect(() => {
        if (productUuid && product && !didPrePopulate.current) {
            didPrePopulate.current = true;
            setCompletedSteps(new Set(steps.map((_, idx) => idx)));
        }
    }, [productUuid, product, steps]);

    useEffect(() => {
        if (!productUuid) return;
        setLoading(true);
        setError('');
        fetch(`${API_URL}/v1/seller/products/${productUuid}`)
            .then(async response => {
                const body = await readApiResponse(response);
                if (!response.ok) {
                    throw new Error(messageFromApiResponse(body, 'Could not load this product.'));
                }
                if (!body.data) {
                    throw new Error('Could not load this product.');
                }
                setProduct(body.data);
            })
            .catch(loadError => setError(loadError.message))
            .finally(() => setLoading(false));
    }, [productUuid]);

    const requestProductPatch = async (payload, {keepalive = false} = {}) => {
        const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive,
        });
        const json = await readApiResponse(res);
        if (!res.ok) throw new Error(messageFromApiResponse(json, 'Failed to save'));
        return json.data;
    };

    const patchProduct = (payload, options) => {
        const save = saveQueue.current
            .catch(() => undefined)
            .then(() => requestProductPatch(payload, options));
        saveQueue.current = save;
        return save;
    };

    const handleAutosave = async payload => {
        setError('');
        setSaving(true);
        try {
            const updated = await patchProduct(payload, {keepalive: true});
            setProduct(updated);
        } catch (saveError) {
            setError(saveError.message);
            throw saveError;
        } finally {
            setSaving(false);
        }
    };

    // Called by step components when user clicks "Next"
    // payload === null means the step handled its own save (Compliance or Media).
    const handleNext = async (payload) => {
        setError('');
        const prevType = product?.type;
        if (payload !== null) {
            setSaving(true);
            try {
                let updated;
                if (!product) {
                    // Create mode: POST to create the product first (Basics step only)
                    const res = await fetch(`${API_URL}/v1/seller/products`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });
                    const json = await readApiResponse(res);
                    if (!res.ok) throw new Error(messageFromApiResponse(json, 'Failed to create product'));
                    updated = json.data;
                } else {
                    updated = await patchProduct(payload);
                }
                // Reset completed steps if product type changed (step count may change)
                if (prevType && updated.type !== prevType) {
                    setCompletedSteps(new Set([0]));
                } else {
                    setCompletedSteps(prev => new Set([...prev, currentStep]));
                }
                setProduct(updated);
            } catch (e) {
                setError(e.message);
                setSaving(false);
                return;
            }
            setSaving(false);
        } else {
            setCompletedSteps(prev => new Set([...prev, currentStep]));
        }
        setCurrentStep(prev => prev + 1);
    };

    // Called by step components when user clicks "Save and Edit Later"
    // payload === null means no main PATCH needed (step handled its own save)
    const handleSaveAndExit = async (payload) => {
        setError('');
        let finalProduct = product;
        if (payload !== null && product) {
            setSaving(true);
            try {
                const updated = await patchProduct(payload);
                setProduct(updated);
                finalProduct = updated;
            } catch (e) {
                setError(e.message);
                setSaving(false);
                return;
            }
            setSaving(false);
        }
        onComplete?.(finalProduct);
    };

    const handleBack = () => {
        setError('');
        setCurrentStep(prev => prev - 1);
    };

    // Jump to any already-completed step by clicking it in the progress bar
    const handleStepClick = (idx) => {
        setError('');
        setCurrentStep(idx);
    };

    // Called by the Compliance step to sync updated compliance/flag data back up
    const handleProductUpdated = (updatedProduct) => {
        setProduct(current => current ? {...current, ...updatedProduct} : updatedProduct);
    };

    const handleTranslationsChange = (translations) => {
        setProduct(current => current ? { ...current, translations } : current);
    };

    const handlePublish = async () => {
        setError('');
        setSaving(true);
        try {
            const updated = await patchProduct({ isAvailable: true });
            setProduct(updated);
            onComplete?.(updated);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDraft = async () => {
        setError('');
        setSaving(true);
        try {
            const updated = await patchProduct({ isAvailable: false });
            setProduct(updated);
            onComplete?.(updated);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const renderStep = () => {
        const stepName = steps[currentStep];
        switch (stepName) {
            case 'Basics':
                return (
                    <ProductWizardBasics
                        product={product}
                        onNext={handleNext}
                        saving={saving}
                        error={error}
                        defaultLanguage={channel?.defaultLanguage}
                        defaultCurrency={channel?.defaultCurrency}
                        onTranslationsChange={handleTranslationsChange}
                        onAutosave={handleAutosave}
                    />
                );
            case 'Details':
                return (
                    <ProductWizardDetails
                        product={product}
                        onNext={handleNext}
                        onBack={handleBack}
                        onSaveAndExit={handleSaveAndExit}
                        saving={saving}
                        error={error}
                        defaultLanguage={channel?.defaultLanguage}
                        onTranslationsChange={handleTranslationsChange}
                        onAutosave={handleAutosave}
                    />
                );
            case 'Compliance':
                return (
                    <ProductWizardCompliance
                        product={product}
                        onNext={handleNext}
                        onBack={handleBack}
                        onSaveAndExit={handleSaveAndExit}
                        onProductUpdated={handleProductUpdated}
                    />
                );
            case 'Media':
                return (
                    <ProductWizardMedia
                        product={product}
                        onNext={handleNext}
                        onBack={handleBack}
                        onSaveAndExit={handleSaveAndExit}
                        saving={saving}
                        error={error}
                        onAutosave={handleAutosave}
                    />
                );
            case 'Stock':
                return (
                    <ProductWizardStock
                        product={product}
                        onNext={handleNext}
                        onBack={handleBack}
                        onSaveAndExit={handleSaveAndExit}
                        onAutosave={handleAutosave}
                        error={error}
                    />
                );
            case 'Shipping':
                return (
                    <ProductWizardShipping
                        product={product}
                        channel={channel}
                        onNext={handleNext}
                        onBack={handleBack}
                        onSaveAndExit={handleSaveAndExit}
                        saving={saving}
                        error={error}
                        onAutosave={handleAutosave}
                    />
                );
            case 'Publish':
                return (
                    <ProductWizardPublish
                        product={product}
                        channel={channel}
                        onPublish={handlePublish}
                        onSaveDraft={handleSaveDraft}
                        onBack={handleBack}
                        onDismiss={() => onComplete?.(product)}
                        saving={saving}
                        error={error}
                    />
                );
            default:
                return null;
        }
    };

    if (loading) {
        return <div>Loading product…</div>;
    }

    if (productUuid && !product) {
        return <Alert type="error">{error || 'Could not load this product.'}</Alert>;
    }

    const content = (
        <>
            <MarketplaceVisibilityBanner />
            <WizardProgressBar
                steps={steps}
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
            />
            {renderStep()}
        </>
    );

    if (modal) {
        return content;
    }

    return (
        <div className="product-wizard">
            <h2>{product ? `Edit: ${product.name}` : 'New Product'}</h2>
            {content}
        </div>
    );
}
